import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientId, useClientEmail, useUpdateEmail } from '@/hooks/client';
import { UpdateEmailModal } from '@/types/modal';
import { Input } from '@/components/molecules/common/Form';
import { ErrorModal, StandardModal, SuccessModal } from '@/components/molecules/common/Modal';
import { isEmail } from '@/utilities/validation';
import styles from './Modal.module.scss';

const View: React.FC<{ modal: UpdateEmailModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { client, onClose } = modal;

  const [email, setEmail] = useState('');
  const clientIdR = useClientId(Number(client.clientId), false);
  const clientEmailR = useClientEmail(client.email, false);
  const submitR = useUpdateEmail(Number(client.clientId));

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      clientIdR.refetch();
      clientEmailR.refetch();
    }
  }, [submitR.kind]);

  if (submitR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.update_email.title')}
        error={submitR.error}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Completed') {
    return (
      <SuccessModal
        title={t('modal.update_email.title')}
        onClose={onClose}
      />
    );
  }

  const errorMessage =
    client.email === email
      ? t('modal.update_email.same_email_address')
      : !isEmail(email) && email != ''
      ? t('modal.update_email.invalid_email_address')
      : undefined;

  const body = (
    <div className={styles.flex100}>
      <div className={styles.height1rem} />
      <div className={styles.text1rem}>{t('modal.update_email.description')}</div>
      <div className={styles.height1rem} />
      <div className={styles.text1rem}>
        {t('modal.update_email.current_email')} <strong>{client.email}</strong>
      </div>
      <div className={styles.height1rem} />
      <Input
        label={t('modal.update_email.new_email')}
        required={true}
        value={email}
        onChange={setEmail}
        isError={errorMessage != null}
        helperText={errorMessage}
      />
    </div>
  );

  return (
    <StandardModal
      title={t('modal.update_email.title')}
      onClose={onClose}
      onSubmit={() => submitR.mutate({ oldEmail: client.email, newEmail: email })}
      submittable={submitR.kind !== 'Loading' && isEmail(email) && errorMessage == null}
      body={body}
      containerStyle={{ maxWidth: 500 }}
      modalName="update_email"
    />
  );
};

export default View;
